/**
 * Floor-state bus — single WebSocket connection feeding live table data
 * to every component on the page.
 *
 * Why a bus and not a per-component hook:
 *   1. One WS connection per page (vs. one per RestaurantCard) — the Vite
 *      proxy was spewing ECONNABORTED noise because we opened ~4 sockets,
 *      each reconnecting on HMR.
 *   2. State stays consistent across cards/modal/simulator — they all read
 *      from the same map.
 *
 * Hydration strategy (alpha):
 *   1. On the first subscribe, fire off a Supabase fetch in parallel with
 *      opening the WS. The fetch resolves with cold-start state if the WS
 *      snapshot hasn't landed yet; otherwise it's a no-op.
 *   2. The WS `tables_snapshot` always wins — it's authoritative because the
 *      Bun server is the writer.
 *   3. Every subsequent `table` event mutates the slice in place.
 *
 * Production hardening (Step 5):
 *   - Aggressive reconnect on visibilitychange / online / focus so mobile
 *     Safari resumes immediately when you swipe back to the tab or hop
 *     from Wi-Fi to 5G.
 *   - `subscribe` message sent on every (re)connect with the set of
 *     restaurant_ids we care about, so the server can scope fan-out.
 */

import { listRestaurants, listTables } from "@/data/restaurants";
import { deriveCapacityLevel, type CapacityLevel } from "@/lib/capacityLevel";
import { env } from "@/lib/env";
import type { Restaurant, RestaurantId, Table } from "@/types/restaurant";

export interface RestaurantFloorState {
  tables: Table[];
  occupiedSeats: number;
  totalSeats: number;
  level: CapacityLevel;
  /** ISO timestamp of the last mutation observed (snapshot or event). */
  lastEventAt: string | null;
}

export type FloorStateMap = Record<RestaurantId, RestaurantFloorState>;

/**
 * Connection lifecycle, exposed so the UI can render "Live" / "Reconnecting"
 * / "Offline" pills. `connecting` and `reconnecting` are distinguished
 * internally but UIs usually collapse them into a single "Reconnecting…"
 * state — both mean "not currently open, working on it".
 */
export type WsStatus = "connecting" | "open" | "reconnecting" | "offline";

type Listener = (state: FloorStateMap) => void;
type StatusListener = (status: WsStatus) => void;

const listeners = new Set<Listener>();
const statusListeners = new Set<StatusListener>();
let state: FloorStateMap = {};
let status: WsStatus =
  typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "connecting";
/** Per-restaurant total seat capacity, hydrated from the restaurants table. */
const restaurantCapacity: Record<RestaurantId, number> = {};
/** Restaurant ids we currently want events for — sent to server as `subscribe`. */
const subscribedIds = new Set<RestaurantId>();
let hydrating = false;
let socket: WebSocket | null = null;
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let wakeListenersAttached = false;

function setStatus(next: WsStatus): void {
  if (status === next) return;
  status = next;
  for (const listener of statusListeners) listener(next);
}

function defaultWsUrl(): string {
  if (env.WS_URL) return env.WS_URL;
  if (typeof window === "undefined") return "ws://localhost:8787/ws";
  // When the page is served by Vite on :5173 directly (laptop or LAN), the
  // Bun server is reachable on :8787 on the same host — skip Vite's proxy.
  // Otherwise (public tunnel like Cloudflare, prod build), route through the
  // same origin and let the proxy / reverse proxy forward "/ws" to Bun.
  const { hostname, port, protocol } = window.location;
  if (port === "5173") {
    return `ws://${hostname || "localhost"}:8787/ws`;
  }
  const wsProto = protocol === "https:" ? "wss:" : "ws:";
  return `${wsProto}//${window.location.host}/ws`;
}

function totalSeatsFor(restaurantId: RestaurantId): number {
  return restaurantCapacity[restaurantId] ?? 0;
}

function recomputeRestaurant(restaurantId: RestaurantId, tables: Table[]): RestaurantFloorState {
  const occupiedSeats = tables
    .filter((t) => t.status === "occupied")
    .reduce((s, t) => s + t.capacity, 0);
  const totalSeats = totalSeatsFor(restaurantId);
  return {
    tables,
    occupiedSeats,
    totalSeats,
    level: deriveCapacityLevel(occupiedSeats, totalSeats),
    lastEventAt: state[restaurantId]?.lastEventAt ?? null,
  };
}

function emit(): void {
  // Hand listeners a fresh reference so React's referential-equality bail-outs
  // still trigger re-renders.
  state = { ...state };
  for (const listener of listeners) listener(state);
}

function ingestTables(rows: Table[], occurredAt: string | null): void {
  const byRestaurant = new Map<RestaurantId, Table[]>();
  for (const t of rows) {
    const bucket = byRestaurant.get(t.restaurant_id) ?? [];
    bucket.push(t);
    byRestaurant.set(t.restaurant_id, bucket);
  }
  const next: FloorStateMap = {};
  for (const [restaurantId, tables] of byRestaurant) {
    const sorted = [...tables].sort((a, b) => a.table_id.localeCompare(b.table_id));
    const slice = recomputeRestaurant(restaurantId, sorted);
    if (occurredAt) slice.lastEventAt = occurredAt;
    next[restaurantId] = slice;
    subscribedIds.add(restaurantId);
  }
  state = next;
  emit();
  // Push the updated subscription set to the server if we're connected.
  sendSubscribe();
}

interface TableEventFrame {
  table_id: string;
  restaurant_id: RestaurantId;
  action: "occupy" | "clear";
  status: "free" | "occupied";
  capacity: number;
  occupied_seats: number;
  total_seats: number;
  level: CapacityLevel;
  occurred_at: string;
}

function applyTableEvent(event: TableEventFrame): void {
  // Keep the per-restaurant capacity cache in sync with the server.
  if (event.total_seats > 0) restaurantCapacity[event.restaurant_id] = event.total_seats;

  const current = state[event.restaurant_id];
  const baseTables = current?.tables ?? [];
  const nextTables = (() => {
    const existing = baseTables.find((t) => t.table_id === event.table_id);
    if (existing) {
      return baseTables.map((t) =>
        t.table_id === event.table_id
          ? { ...t, status: event.status, updated_at: event.occurred_at }
          : t,
      );
    }
    return [
      ...baseTables,
      {
        table_id: event.table_id,
        restaurant_id: event.restaurant_id,
        name: event.table_id,
        capacity: event.capacity,
        status: event.status,
        created_at: event.occurred_at,
        updated_at: event.occurred_at,
      } satisfies Table,
    ];
  })();

  state[event.restaurant_id] = {
    tables: nextTables,
    occupiedSeats: event.occupied_seats,
    totalSeats: event.total_seats,
    level: event.level,
    lastEventAt: event.occurred_at,
  };
  emit();
}

async function hydrateFromSupabase(): Promise<void> {
  if (hydrating || Object.keys(state).length > 0) return;
  hydrating = true;
  try {
    // Fetch in parallel — restaurants gives us capacity_seats per venue, tables
    // gives us the actual floor plan.
    const [restaurants, tables] = await Promise.all([listRestaurants(), listTables()]);
    for (const r of restaurants as Restaurant[]) {
      restaurantCapacity[r.id] = r.capacity_seats;
      subscribedIds.add(r.id);
    }
    // If the WS snapshot beat us, don't clobber it — it's more authoritative.
    if (Object.keys(state).length === 0) ingestTables(tables, null);
    // Make sure the server knows our final id set, even if `ingestTables`
    // didn't run (because the snapshot already populated state).
    sendSubscribe();
  } catch (err) {
    // Log but don't throw — the WS snapshot will hydrate us shortly anyway.
    console.warn("[floorBus] Supabase hydration failed:", err);
  } finally {
    hydrating = false;
  }
}

function sendSubscribe(): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  if (subscribedIds.size === 0) return;
  try {
    socket.send(
      JSON.stringify({
        type: "subscribe",
        restaurant_ids: [...subscribedIds],
      }),
    );
  } catch {
    // ignore — close handler will reconnect
  }
}

function connect(): void {
  if (typeof window === "undefined") return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  attachWakeListeners();
  setStatus("connecting");

  const url = defaultWsUrl();
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.addEventListener("open", () => {
    reconnectAttempt = 0;
    setStatus("open");
    sendSubscribe();
  });

  ws.addEventListener("message", (msg) => {
    let payload: unknown;
    try {
      payload = JSON.parse(typeof msg.data === "string" ? msg.data : "");
    } catch {
      return;
    }
    if (!payload || typeof payload !== "object") return;
    const frame = payload as { type?: string };
    if (frame.type === "table") {
      const event = (payload as { event?: TableEventFrame }).event;
      if (event) applyTableEvent(event);
    } else if (frame.type === "tables_snapshot") {
      const rows = (payload as { tables?: Table[] }).tables;
      if (Array.isArray(rows)) ingestTables(rows, null);
    }
  });

  ws.addEventListener("close", () => {
    if (socket === ws) socket = null;
    // If the browser knows we're offline, prefer that label over "reconnecting";
    // the wake listeners will flip us back to "connecting" when the network
    // returns.
    setStatus(typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "reconnecting");
    scheduleReconnect();
  });

  ws.addEventListener("error", () => {
    ws.close();
  });
}

function scheduleReconnect(): void {
  if (reconnectTimer) return;
  reconnectAttempt += 1;
  const delay = Math.min(15_000, 1000 * 2 ** Math.min(reconnectAttempt, 5));
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

/**
 * Triggered when the user signals the tab is "live" again — page becomes
 * visible, network comes back online, or window regains focus. We bypass the
 * exponential backoff (resetting `reconnectAttempt`) so the first attempt
 * fires instantly. Mobile browsers throttle background WebSockets aggressively;
 * without these wake handlers, a phone that was idle for 30 seconds could
 * take 15+ seconds to reconnect.
 */
function wakeAndReconnect(): void {
  if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
    reconnectAttempt = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    connect();
  }
}

function attachWakeListeners(): void {
  if (wakeListenersAttached || typeof window === "undefined") return;
  wakeListenersAttached = true;

  const onVisibility = () => {
    if (document.visibilityState === "visible") wakeAndReconnect();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", wakeAndReconnect);
  window.addEventListener("focus", wakeAndReconnect);
  window.addEventListener("pageshow", wakeAndReconnect);
  // Flip the status the moment the OS reports the network dropped, even before
  // the socket has noticed. Snappier indicator.
  window.addEventListener("offline", () => setStatus("offline"));
}

/**
 * Subscribe to floor-state updates. The first subscriber boots the
 * connection AND the Supabase fetch; the last unsubscribe leaves the
 * connection open (cheap) so a quick remount doesn't pay the WS handshake
 * cost twice.
 */
export function subscribeFloor(listener: Listener): () => void {
  listeners.add(listener);
  void hydrateFromSupabase();
  if (!socket) connect();
  // Hand the new subscriber the current snapshot synchronously so React
  // doesn't paint an empty frame first.
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

export function getFloorState(): FloorStateMap {
  return state;
}

/**
 * Boot the WebSocket connection without subscribing to floor data. Surfaces
 * that only render the connection-status pill (the admin / operator headers)
 * call this so the socket actually opens — otherwise the pill sits on its
 * initial "connecting" state forever, since only `subscribeFloor` boots the
 * connection. Idempotent: `connect()` no-ops if a socket is already open or
 * connecting.
 */
export function ensureConnected(): void {
  if (typeof window === "undefined") return;
  connect();
}

/**
 * Subscribe to WebSocket connection-status transitions. Invokes the listener
 * synchronously with the current status so React subscribers don't paint an
 * incorrect first frame.
 */
export function subscribeWsStatus(listener: StatusListener): () => void {
  statusListeners.add(listener);
  listener(status);
  return () => {
    statusListeners.delete(listener);
  };
}

export function getWsStatus(): WsStatus {
  return status;
}
